import { RTL_BASE_LANGUAGES } from './direction'
import { LOCALE_COOKIE_NAME, LOCALE_STORAGE_KEY, SUPPORTED_LOCALES } from './types'
import { PLAYER_LOCALE_SESSION_KEY } from './locale-preference'

/**
 * Sets <html lang dir> from the saved language before the page paints
 * (LANG-09, RTL-01).
 *
 * The root layout stays static — reading cookies() there would make every
 * page dynamic — so the server always renders lang="en" dir="ltr". Without
 * this, every hard load showed an Arabic reader a left-to-right page until
 * React hydrated. It runs inline in <head>, so it is plain ES5 text with no
 * imports; it applies the same rules as toSupportedLocale and directionFor
 * (locale-bootstrap.spec.tsx executes it against the same cases), and it never
 * throws — storage can be blocked.
 */
/**
 * While on <html>, the body is hidden: the saved language is not English and
 * its catalogue is not on screen yet. A cold Arabic load used to paint every
 * word in English inside a right-to-left layout until the catalogue arrived.
 * The provider removes it once the catalogue is applied or has failed
 * (releasePaintHold); the script removes it after PAINT_HOLD_CAP_MS whatever
 * happens, so no page can stay hidden.
 */
export const PAINT_HOLD_CLASS = 'hf-i18n-pending'
export const PAINT_HOLD_CAP_MS = 3000

/** Show the page: the language on screen is the one it will keep. */
export function releasePaintHold(): void {
  if (typeof document === 'undefined') return
  document.documentElement.classList.remove(PAINT_HOLD_CLASS)
}

export const LOCALE_BOOTSTRAP_SCRIPT = `(function(){try{
var supported=${JSON.stringify(SUPPORTED_LOCALES)};
var rtl=${JSON.stringify([...RTL_BASE_LANGUAGES])};
var pick=function(v){if(typeof v!=='string')return null;var b=v.trim().toLowerCase().split(/[-_]/)[0];return supported.indexOf(b)>=0?b:null;};
var chosen=null;
if(location.pathname.indexOf('/player')===0){try{chosen=pick(window.sessionStorage.getItem(${JSON.stringify(PLAYER_LOCALE_SESSION_KEY)}));}catch(e){}}
if(!chosen){try{chosen=pick(window.localStorage.getItem(${JSON.stringify(LOCALE_STORAGE_KEY)}));}catch(e){}}
if(!chosen){var m=document.cookie.match(/(?:^|;\\s*)${LOCALE_COOKIE_NAME}=([^;]*)/);if(m){try{chosen=pick(decodeURIComponent(m[1]));}catch(e){}}}
if(!chosen)return;
var root=document.documentElement;
root.setAttribute('lang',chosen);
root.setAttribute('dir',rtl.indexOf(chosen)>=0?'rtl':'ltr');
if(chosen!=='en'){root.classList.add(${JSON.stringify(PAINT_HOLD_CLASS)});setTimeout(function(){root.classList.remove(${JSON.stringify(PAINT_HOLD_CLASS)});},${PAINT_HOLD_CAP_MS});}
}catch(e){}})();`

/** Render inside the root layout's <head>, before anything paints. */
export function LocaleBootstrapScript() {
  return (
    <>
      <script id="hf-locale-bootstrap" dangerouslySetInnerHTML={{ __html: LOCALE_BOOTSTRAP_SCRIPT }} />
      <style id="hf-locale-paint-hold" dangerouslySetInnerHTML={{ __html: `html.${PAINT_HOLD_CLASS} body{visibility:hidden}` }} />
    </>
  )
}
